import React, { useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { StoreProvider } from '@common/StoreProvider';
import { TRACK_EVENTS } from '@common/constants';
import {
  useAnalytics,
  useCreateAccountCompany,
  useGetAccountCompanies,
  useGetAccountUsers,
} from '@common/hooks';
import InviteAccountMemberModal from './component';
import { reducer, initialState } from './state/reducer';
import { ACTIONS } from './state/actions';
import { useCreateAccountUsers, useGetAccountRoles } from './hooks';

const InviteAccountMemberModalContainer = ({ onSubmit, show, ...rest }) => {
  const [state, dispatch] = useReducer(reducer, show, initialState);
  const { trackEvent } = useAnalytics();

  const createAccountUsersRef = useRef();

  const { createAccountUsersRequest } = useCreateAccountUsers({
    onSubmit,
    dispatch,
  });

  const handleCreateAccountCompanySuccess = (companyId) => {
    createAccountUsersRef.current.params.defaultCompanyId = companyId;
    createAccountUsersRequest(createAccountUsersRef.current.params);
  };

  const handleCreateAccountCompanyError = () => {
    dispatch({
      type: ACTIONS.SET_SHOW_COMPANY_CREATE_ERROR,
      payload: true,
    });
    dispatch({
      type: ACTIONS.SET_IS_SAVING,
      payload: false,
    });
  };

  const { createAccountCompanyRequest } = useCreateAccountCompany({
    onError: handleCreateAccountCompanyError,
    onSuccess: handleCreateAccountCompanySuccess,
  });

  const { accountCompanies, onChangeCompanyInput } =
    useGetAccountCompanies(true);

  const { activeAccountRoles, handleRoleInputChange } = useGetAccountRoles();

  const { accountUsersData, handleEmailInputChange } = useGetAccountUsers({
    includeAllMembers: true,
  });

  const handleSubmitInviteMembers = ({
    emails,
    isNewCompany,
    selectedAccountAccessLevels,
    selectedCompany,
    selectedRole,
  }) => {
    dispatch({
      type: ACTIONS.SET_SHOW_COMPANY_CREATE_ERROR,
      payload: false,
    });
    dispatch({
      type: ACTIONS.SET_IS_SAVING,
      payload: true,
    });
    const defaultRoleId = selectedRole?.id;
    const isSelectedCompany = selectedCompany !== null;
    const defaultCompanyId = selectedCompany?.id;
    const companyName = selectedCompany?.name;
    const isActiveCompany = selectedCompany?.status === 'active';

    const params = {
      accessLevels: selectedAccountAccessLevels,
      defaultCompanyId,
      defaultRoleId,
      emails,
    };

    createAccountUsersRef.current = {
      params,
    };

    if (!isSelectedCompany || isActiveCompany) {
      createAccountUsersRequest(params);
      return;
    }

    if (isNewCompany) {
      createAccountCompanyRequest(companyName);
      trackEvent(
        `${TRACK_EVENTS.SCOPE.ACCOUNT_ADMIN}.${TRACK_EVENTS.NAME.COMPANY}.${TRACK_EVENTS.NAME.CREATE}`,
        { origin: 'invite' }
      );
    }
  };

  return (
    <StoreProvider state={state} dispatch={dispatch}>
      <InviteAccountMemberModal
        {...rest}
        accountUsers={accountUsersData ? accountUsersData.results : []}
        companies={accountCompanies}
        onChangeCompanyInput={onChangeCompanyInput}
        onChangeEmailInput={handleEmailInputChange}
        onChangeRoleInput={handleRoleInputChange}
        onSubmitInviteMembers={handleSubmitInviteMembers}
        roles={activeAccountRoles}
        show={show}
      />
    </StoreProvider>
  );
};

InviteAccountMemberModalContainer.propTypes = {
  /**  handler executed when the user clicks on the Cancel button or on the Close Dialog cross */
  onClose: PropTypes.func.isRequired,
  /** handler executed after the user invite an account member */
  onSubmit: PropTypes.func.isRequired,
  /** Boolean property to set the visibility of the Modal */
  show: PropTypes.bool,
};

InviteAccountMemberModalContainer.defaultProps = {
  show: false,
};

export default InviteAccountMemberModalContainer;
